import React, { useContext, useEffect, useState } from "react";
import PageWrapper from "../../components/PageWrapper";
import { useTranslation } from "react-i18next";
import CustomInput from "../../components/CustomInput";
import { UserAuth } from "../../utils/AuthContext";
import { getPaymentCode } from "../../services/PaymentCodeService";
import { getAllProducts } from "../../services/productServices";
import { formatDate } from "../../utils/toDate";
import useDebounce from "../../utils/useDebounce";
import { getStudentByCode } from "../../services/StudentServices";
import ErrorBox from "../../components/ErrorBox";
import HotdropdownV2 from "../../components/HotdropdownV2";
import SupplieBooks from "./SupplieBooks";
import SupplieUniform from "./SupplieUniform";
import axiosClient from "../../services/axiosClient";
import { getAllKassas } from "../../services/kassa/kassaServices";
import Loader from "../../components/Loader";
import InformationBox from "../../components/InformationBox";

const AddSuppliesOrder = () => {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);

  const [orderDetails, setOrderDetails] = useState({
    orderNumber: "",
    date: new Date(),
    companyName: "",
    createdBy: "",
    notes: "",
  });
  const [receiptNumber, setReceiptNumber] = useState("");

  const [loadingBooks, setLoadingBooks] = useState(false);
  const [studentCode, setStudentCode] = useState(null);
  const studentIdValue = useDebounce(studentCode);
  const [student, setStudent] = useState(undefined);
  const [studentFetchStatus, setStudentFetchStatus] = useState("");
  const [studentFetchError, setStudentFetchError] = useState("");

  const [booksData, setBooksData] = useState([]);
  const [uniformsData, setUniformsData] = useState([]);
  const [orderData, setOrderData] = useState({ orderNumber: null, date: null });
  const [selectedCurrency] = useState({
    title: "usd",
    value: "USD",
  });
  const [selectedSupplie, setSelectedSupplie] = useState("books");
  const [loadingUniform, setLoadingUniform] = useState(true);
  const [kassaLoading, setKassasLoading] = useState(false);
  const [kassas, setKassas] = useState([]);
  const [selectedKassa, setSelectedKassa] = useState(null);

  const supplieOptions = [
    { id: "books", title: t("books") },
    { id: "uniform", title: t("uniform") },
  ];

  const fetchKassas = async () => {
    setKassasLoading(true);
    try {
      const response = await getAllKassas({
        page: 1,
        limit: 100,
      });

      if (response.status === 200) {
        setKassas(response.data.kassas);

        // Set first kassa as default if available
        if (response.data.kassas.length > 0) {
          const firstKassa = response.data.kassas[0];
          setSelectedKassa({
            title: firstKassa.name,
            value: firstKassa.id,
            kassaData: firstKassa,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching Kassas:", error);
      // setError("failedToFetchKassas");
    } finally {
      setKassasLoading(false);
    }
  };

  // Function to get order number
  const getOrderNumber = async () => {
    try {
      const response = await getPaymentCode();
      if (response.status == 200) {
        setReceiptNumber(response?.data?.code);
        setOrderData((prev) => ({
          ...prev,
          orderNumber: response?.data?.code,
        }));
        setOrderDetails((prev) => ({
          ...prev,
          orderNumber: response?.data?.code,
          companyName: userData.organizationName,
          createdBy: userData.username,
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Get all books from API (modified to fetch all books at once)
  const getAllBooks = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingBooks(true);

      const response = await getAllProducts({
        page: 1,
        limit: 1000, // Fetch all books at once
        organizationId: organizationId,
        productType: "book",
      });

      if (response.data?.products) {
        // Group books by academic level
        const groupedBooks = groupBooksByAcademicLevel(response.data.products);
        setBooksData(groupedBooks); // Initially display all books
      }
    } catch (err) {
      console.error("Failed to fetch books:", err);
    } finally {
      setLoadingBooks(false);
    }
  };

  const getAllUniforms = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingUniform(true);
      const response = await axiosClient.get("/productRuler", {
        params: {
          organizationId,
          limit: 10000,
        },
      });

      if (response.status === 200 && response.data) {
        const items = response.data.productRulers?.map((item) => ({
          id: item.id,
          name: item.name,
          color: item.color,
          sizes: item.productRulerItem?.map((item) => ({
            id: item.id,
            size: item.size,
            sellPrice: item.sellPrice,
            notes: item.notes,
            amountInStock: item.quantity,
            quantity: "",
          })),
        }));
        setUniformsData(items);
      }
    } catch (err) {
      console.error("Failed to fetch uniforms:", err);
    } finally {
      setLoadingUniform(false);
    }
  };

  // Helper function to group books by academic level
  const groupBooksByAcademicLevel = (books) => {
    const grouped = books.reduce((acc, book) => {
      // Group by class ID but store the class name for display
      const groupId = book.schoolClassId || "ungrouped";
      const groupName = book.schoolClass?.name || groupId;

      if (!acc[groupId]) {
        acc[groupId] = {
          id: groupId,
          category: groupName, // Use class name for display
          books: [],
        };
      }
      acc[groupId].books.push({
        ...book,
        quantity: "",
        price: book.price,
        totalPrice: "",
        academicLevel: groupName,
        productSubType: t(book.productSubType),
      });
      return acc;
    }, {});

    return Object.values(grouped);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setStudentCode(query);
  };

  // Reset data after successful order
  const resetToOriginalData = () => {
    setStudentCode(null);
    setStudent(undefined);
    getOrderNumber();
  };

  async function getStudentInfo(code = "") {
    try {
      const response = await getStudentByCode(code);
      setStudent(response.data);
      setStudentFetchStatus("");
      // TODO: handle error
    } catch (error) {
      setStudentFetchStatus("error");
      if (error.response?.status === 404) {
        setStudentFetchError("studentNotFound");
      }
    }
  }

  const handleResetSearchInput = () => {
    setStudentCode("");
    setStudent(null);
    setStudentFetchError("");
  };

  useEffect(() => {
    if (selectedSupplie === "books" && booksData.length === 0) {
      getAllBooks();
      return;
    }

    if (selectedSupplie === "uniform" && uniformsData.length === 0) {
      getAllUniforms();
    }
  }, [selectedSupplie]);

  useEffect(() => {
    getOrderNumber();
  }, []);

  useEffect(() => {
    if (studentIdValue !== null && studentIdValue !== "") {
      setStudent(null);
      setStudentFetchStatus("pendding");
      getStudentInfo(studentIdValue);
      return;
    }
  }, [studentIdValue]);

  useEffect(() => {
    fetchKassas();
  }, []);

  const transformKassasForDropdown = (kassasData) => {
    return kassasData.map((kassa) => ({
      title: kassa.name,
      value: kassa.id,
      kassaData: kassa,
    }));
  };

  const isFirstTimeAddingUser = student === undefined;

  const showKassaError =
    student !== null && !isFirstTimeAddingUser && !selectedKassa?.value;

  return (
    <PageWrapper withBackButton title={t("supplies")}>
      {student === undefined && (
        <InformationBox>{t("pleaseSelectStudent")}</InformationBox>
      )}
      {student === null && <ErrorBox>{t("pleaseSelectStudent")}</ErrorBox>}
      {showKassaError && <ErrorBox>{t("pleaseSelectKassa")}</ErrorBox>}

      <div className="o-column o-column--gap-size_200 u-width_full-width u-margin--top_700">
        <label>{t("supplieType")}</label>
        <div>
          <HotdropdownV2
            initOptions={supplieOptions}
            selected={supplieOptions.find(
              (belonging) => belonging.id === selectedSupplie
            )}
            setSelected={(selected) => {
              setSelectedSupplie(selected.id);
            }}
            optionsName="id"
          />
        </div>
      </div>
      <div className="o-column o-column--gap-size_200 u-width_full-width u-margin--top_300">
        <label>{t("selectKassa")}:</label>
        <div>
          <HotdropdownV2
            initOptions={transformKassasForDropdown(kassas)}
            selected={selectedKassa?.title || ""}
            setSelected={(option) => setSelectedKassa(option)}
            optionsName="kassas"
          />
        </div>
      </div>
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500 u-margin--top_500">
        <div className="o-column-row-switcher-reversed o-column--horizontal-align_space-between o-row--gap-size_300">
          <div className="c-search-bar">
            <section className="c-search-bar-label-input">
              <CustomInput
                value={studentCode}
                onChange={handleSearchChange}
                resetSearch={handleResetSearchInput}
                title={t("studentId")}
              />
            </section>
          </div>

          <div className="o-row o-row--gap-size_300">
            <CustomInput
              title={t("orderNumber") + ": "}
              value={orderData?.orderNumber || receiptNumber}
              flex={false}
              readonly
              loading={loadingBooks}
            />
            <CustomInput
              type="date"
              title={t("Date") + ": "}
              value={formatDate(orderDetails.date)}
              onChange={(e) => {
                setOrderDetails((prev) => ({
                  ...prev,
                  date: new Date(e.target.value),
                }));
              }}
              flex={false}
              loading={loadingBooks}
            />
          </div>
        </div>

        <div className="o-row o-row--gap-size_300 o-row--vertical-align_center u-margin--top_400">
          <p className={`u-padding_0 u-margin_0 u-min-width-specific_150`}>
            {t("studentName")}:
          </p>
          {studentFetchStatus === "pendding" && (
            <span className={`t-bold_semi-bold`}>
              {t("loadingStudentInfo")}
            </span>
          )}
          {student !== null && (
            <span className={`t-bold_semi-bold`}>{student?.fullName}</span>
          )}
          {studentFetchStatus === "error" && (
            <span className={`t-bold_semi-bold negative-value`}>
              {t(studentFetchError)}
            </span>
          )}
        </div>
        {selectedSupplie === "books" && (
          <SupplieBooks
            booksData={booksData}
            student={student}
            onSuccess={() => {
              getAllBooks();
              resetToOriginalData();
            }}
            selectedCurrency={selectedCurrency}
            orderDetails={orderDetails}
            loadingBooks={loadingBooks}
            kassa={selectedKassa?.value}
            receiptNumber={ receiptNumber}
          />
        )}

        {selectedSupplie === "uniform" && (
          <SupplieUniform
            uniformsData={uniformsData}
            student={student}
            onSuccess={() => {
              getAllUniforms();
              resetToOriginalData();
            }}
            selectedCurrency={selectedCurrency}
            orderDetails={orderDetails}
            loadingUniform={loadingUniform}
            kassa={selectedKassa?.value}
            receiptNumber={receiptNumber}
          />
        )}
      </div>
    </PageWrapper>
  );
};

export default AddSuppliesOrder;
